import React, { useState, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { useLanguage } from '../../context/LanguageContext';
import { prefetchRoute } from '../../routes/prefetch';
import useFocusTrap from '../../hooks/useFocusTrap';

// Sidebar 5 gruba bölündü (Tur 286 — sadeleştirme):
// main (5, hep açık) + operations + staff + commerce + system (4 collapsible).
// Yönetim sayfaları (tax-groups/feature-flags/white-label/printers/webhooks/platform-management/
// audit-logs/security/trash/gdpr/stock-history) sidebar'dan kaldırıldı; Settings → Yönetim tab'ından erişiliyor.
const allNavItems = [
  // ── Ana operasyon (hep açık) ──
  { to: '/',                tKey: 'nav_dashboard',    icon: '📊', roles: ['admin'], feature: null, group: 'main' },
  { to: '/tables',          tKey: 'nav_tables',       icon: '🍽️', roles: ['admin', 'waiter'], feature: null, group: 'main' },
  { to: '/orders',          tKey: 'nav_orders',       icon: '📋', roles: ['admin', 'waiter'], feature: null, group: 'main' },
  { to: '/kitchen',         tKey: 'nav_kitchen',      icon: '👨‍🍳', roles: ['admin', 'kitchen'], feature: 'kitchen_display', group: 'main' },
  { to: '/pos',             tKey: 'nav_pos',          icon: '🖥️', roles: ['admin', 'cashier'], feature: null, group: 'main' },
  // ── Operasyon (collapsible) ──
  { to: '/menu-management', tKey: 'nav_menu',         icon: '📖', roles: ['admin'], feature: null, group: 'operations' },
  { to: '/stock',           tKey: 'nav_stock',        icon: '📦', roles: ['admin'], feature: 'stock_management', group: 'operations' },
  { to: '/customers',       tKey: 'nav_customers',    icon: '👥', roles: ['admin'], feature: null, group: 'operations' },
  { to: '/reservations',    tKey: 'nav_reservations', icon: '📅', roles: ['admin'], feature: null, group: 'operations' },
  { to: '/payments',        tKey: 'nav_payments',     icon: '💰', roles: ['admin'], feature: null, group: 'operations' },
  { to: '/delivery-orders', tKey: 'nav_delivery',     icon: '🚚', roles: ['admin'], feature: 'delivery_orders', group: 'operations' },
  { to: '/couriers',        tKey: 'nav_couriers',     icon: '🛵', roles: ['admin'], feature: 'couriers', group: 'operations' },
  { to: '/refunds',         tKey: 'nav_refunds',      icon: '💸', roles: ['admin'], feature: 'refunds', group: 'operations' },
  // ── Personel ──
  { to: '/hr',              tKey: 'nav_hr',           icon: '👤', roles: ['admin'], feature: null, group: 'staff' },
  { to: '/users',           tKey: 'nav_users',        icon: '👥', roles: ['admin'], feature: null, group: 'staff' },
  { to: '/shifts',          tKey: 'nav_shifts',       icon: '📅', roles: ['admin'], feature: null, group: 'staff' },
  { to: '/waiter-stats',    tKey: 'nav_waiterStats',  icon: '📈', roles: ['admin'], feature: 'waiter_management', group: 'staff' },
  // ── Ticari & Mali ──
  { to: '/recipes',             tKey: 'nav_recipes',           icon: '🧾', roles: ['admin'], feature: 'recipe_system', group: 'commerce' },
  { to: '/suppliers',           tKey: 'nav_suppliers',         icon: '🏭', roles: ['admin'], feature: 'suppliers', group: 'commerce' },
  { to: '/purchase-orders',     tKey: 'nav_purchase_orders',   icon: '🛒', roles: ['admin'], feature: 'purchase_orders', group: 'commerce' },
  { to: '/cash-transactions',   tKey: 'nav_cash_transactions', icon: '💵', roles: ['admin'], feature: 'cash_transactions', group: 'commerce' },
  { to: '/promo-codes',         tKey: 'nav_promo',             icon: '🎫', roles: ['admin'], feature: 'promo_codes', group: 'commerce' },
  { to: '/gift-vouchers',       tKey: 'nav_gifts',             icon: '🎁', roles: ['admin'], feature: 'gift_vouchers', group: 'commerce' },
  // ── Yazarkasa Yönetim ──
  { to: '/pos-products',         tKey: 'nav_posProducts',         icon: '🏷️', roles: ['admin'], feature: null, group: 'pos_admin' },
  { to: '/pos-reports',          tKey: 'nav_posReports',          icon: '📑', roles: ['admin'], feature: null, group: 'pos_admin' },
  { to: '/pos-receipt-settings', tKey: 'nav_posReceiptSettings',  icon: '🧾', roles: ['admin'], feature: null, group: 'pos_admin' },
  // ── Sistem ──
  { to: '/reports',             tKey: 'nav_reports',     icon: '📊', roles: ['admin'], feature: 'advanced_analytics', group: 'system' },
  { to: '/settings',            tKey: 'nav_settings',    icon: '⚙️', roles: ['admin'], feature: null, group: 'system' },
  { to: '/my-subscription',     tKey: 'nav_subscription', icon: '💳', roles: ['admin'], feature: null, group: 'system' },
];

// Collapsible grup tanımları (render sırası + i18n etiketi)
const COLLAPSIBLE_GROUPS = [
  { key: 'operations', tKey: 'navGroupOperations', fallback: 'Operasyon' },
  { key: 'staff',      tKey: 'navGroupStaff',      fallback: 'Personel' },
  { key: 'commerce',   tKey: 'navGroupCommerce',   fallback: 'Ticari & Mali' },
  { key: 'pos_admin',  tKey: 'navGroupPosAdmin',   fallback: 'Yazarkasa Yönetim' },
  { key: 'system',     tKey: 'navManagementSystem', fallback: 'Sistem' },
];

const roleKeys = { admin: 'role_admin', waiter: 'role_waiter', kitchen: 'role_kitchen', cashier: 'role_cashier' };
const roleColors = {
  admin:   'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  waiter:  'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  kitchen: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  cashier: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { hasFeature, loading: subLoading } = useSubscription();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  useFocusTrap(open, panelRef, { onEscape: () => setOpen(false) });
  // md+ viewport'ta sidebar gizle/göster (tablet/desktop) — localStorage hatırlar.
  const [desktopHidden, setDesktopHidden] = useState(() => {
    try { return localStorage.getItem('restaflex_sidebar_desktop_hidden') === '1'; }
    catch { return false; }
  });
  const toggleDesktopHidden = () => {
    setDesktopHidden(prev => {
      const next = !prev;
      try { localStorage.setItem('restaflex_sidebar_desktop_hidden', next ? '1' : '0'); } catch {}
      return next;
    });
  };
  // Collapsible grupların açık/kapalı durumu — localStorage'da hatırla.
  // Default: 'operations' açık (sık kullanılır), diğerleri kapalı.
  const [openGroups, setOpenGroups] = useState(() => {
    const init = {};
    for (const g of COLLAPSIBLE_GROUPS) {
      try {
        const stored = localStorage.getItem(`restaflex_sidebar_${g.key}`);
        init[g.key] = stored === null ? (g.key === 'operations' || g.key === 'pos_admin') : stored === '1';
      } catch { init[g.key] = g.key === 'operations' || g.key === 'pos_admin'; }
    }
    return init;
  });
  const toggleGroup = (key) => {
    setOpenGroups(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(`restaflex_sidebar_${key}`, next[key] ? '1' : '0'); } catch {}
      return next;
    });
  };

  // Impersonation session: chain_admin entered a branch via "Şubeye Gir" button.
  // Controls "← Zincir Paneline Dön" banner visibility.
  const isImpersonatedBranch = !!sessionStorage.getItem('restaflex_branch_token');

  // Any admin that belongs to a chain (direct login OR impersonation).
  // Controls hiding of "Aboneliğim" menu item — subscription is managed by chain admin.
  const isChainManagedBranch = !!(user?.chain_id);

  const navItems = allNavItems.filter(item => {
    if (!user || !item.roles.includes(user.role)) return false;
    if (isChainManagedBranch && item.to === '/my-subscription') return false;
    return true;
  });
  const mainItems = navItems.filter(i => (i.group || 'main') === 'main');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const close = () => setOpen(false);

  const isLocked = (item) => {
    if (!item.feature) return false;
    if (user?.role === 'superadmin') return false;
    if (subLoading) return false;
    return !hasFeature(item.feature);
  };

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setOpen(true)}
        className="md:hidden fixed top-3.5 left-4 z-50 w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center shadow-lg active:bg-blue-800 transition-colors"
        aria-label={t('openMenu') || 'Menüyü aç'}
        aria-expanded={open}
        aria-controls="main-sidebar"
      >
        <span className="text-xl" aria-hidden="true">☰</span>
      </button>

      {/* Desktop/tablet "show sidebar" button — sadece md+ ve sidebar gizliyken */}
      {desktopHidden && (
        <button
          onClick={toggleDesktopHidden}
          className="hidden md:flex fixed top-3 left-3 z-40 w-9 h-9 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl items-center justify-center shadow-md hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300 transition-colors"
          aria-label={t('showSidebar') || 'Menüyü göster'}
          aria-expanded={false}
          aria-controls="main-sidebar"
          title={t('showSidebar') || 'Menüyü göster'}
        >
          <span aria-hidden="true">▶</span>
        </button>
      )}

      {/* Backdrop */}
      {open && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={close}
        />
      )}

      {/* Sidebar panel */}
      <aside
        ref={panelRef}
        id="main-sidebar"
        role={open ? 'dialog' : 'navigation'}
        aria-modal={open ? 'true' : undefined}
        aria-label={open ? undefined : (t('navMainMenu') || 'Ana menü')}
        aria-labelledby={open ? 'sidebar-nav-title' : undefined}
        className={`
        fixed md:relative top-0 left-0 h-full z-50
        w-64 flex flex-col
        bg-white dark:bg-gray-900
        border-r border-gray-200 dark:border-gray-800
        shadow-2xl md:shadow-none
        transform transition-transform duration-300 ease-in-out
        ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        ${desktopHidden ? 'md:hidden' : ''}
      `}>
        {/* Logo */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center text-white text-lg">🍴</div>
            <div>
              <div id="sidebar-nav-title" className="font-black text-gray-900 dark:text-white text-sm leading-tight">RestaFlex</div>
              <div className="text-[10px] text-gray-400">{t('navManagementPlatform') || 'Yönetim Platformu'}</div>
            </div>
          </div>
          <button
            onClick={close}
            className="md:hidden w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500"
          >✕</button>
          <button
            onClick={toggleDesktopHidden}
            className="hidden md:flex w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 items-center justify-center text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            aria-label={t('hideSidebar') || 'Menüyü gizle'}
            title={t('hideSidebar') || 'Menüyü gizle'}
          >
            <span aria-hidden="true">◀</span>
          </button>
        </div>

        {/* User info */}
        {user && (
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-700 dark:text-blue-300 font-bold text-sm">
                {user.name?.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{user.name}</div>
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${roleColors[user.role] || ''}`}>
                  {t(roleKeys[user.role] || 'role_admin')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Chain branch banner */}
        {isImpersonatedBranch && (
          <div className="px-3 py-2 bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-200 dark:border-emerald-800">
            <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold mb-1.5">
              <span aria-hidden="true">🏢</span> {t('sidebarChainImpersonationBanner') || 'Zincir Yönetimi Altında'}
            </div>
            <button
              onClick={() => window.close()}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors"
            >
              <span aria-hidden="true">←</span> {t('sidebarChainReturnToPanel') || 'Zincir Paneline Dön'}
            </button>
          </div>
        )}

        {/* Nav — UI-01: ana grup + collapsible system grubu */}
        <nav className="flex-1 overflow-y-auto sidebar-scroll py-3 px-3 space-y-0.5">
          {mainItems.map(item => {
            const locked = isLocked(item);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={close}
                onMouseEnter={() => prefetchRoute(item.to)}
                onFocus={() => prefetchRoute(item.to)}
                onTouchStart={() => prefetchRoute(item.to)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all active:scale-[0.98] ${
                    isActive
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-semibold'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                  }`
                }
              >
                <span className="text-lg w-6 text-center flex-shrink-0">{item.icon}</span>
                <span className="flex-1">{t(item.tKey)}</span>
                {locked && (
                  <span className="text-xs text-amber-500 dark:text-amber-400 flex-shrink-0" title={t('sidebarLockedFeatureTitle') || 'Bu özellik planınıza dahil değil'}>🔒</span>
                )}
              </NavLink>
            );
          })}

          {/* Collapsible grupları sırayla render et */}
          {COLLAPSIBLE_GROUPS.map(grp => {
            const items = navItems.filter(i => i.group === grp.key);
            if (items.length === 0) return null;
            const isOpen = !!openGroups[grp.key];
            const label = t(grp.tKey) || grp.fallback;
            return (
              <React.Fragment key={grp.key}>
                <button
                  onClick={() => toggleGroup(grp.key)}
                  aria-expanded={isOpen}
                  aria-controls={`sidebar-group-${grp.key}`}
                  className="w-full mt-3 flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <span className="text-base">{isOpen ? '▾' : '▸'}</span>
                  <span className="flex-1 text-left">{label}</span>
                  <span className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded-full">{items.length}</span>
                </button>
                {isOpen && (
                  <div id={`sidebar-group-${grp.key}`} role="group" aria-label={label} className="space-y-0.5">
                    {items.map(item => {
                      const locked = isLocked(item);
                      return (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          onClick={close}
                          onMouseEnter={() => prefetchRoute(item.to)}
                          onFocus={() => prefetchRoute(item.to)}
                          onTouchStart={() => prefetchRoute(item.to)}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all active:scale-[0.98] ${
                              isActive
                                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-semibold'
                                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                            }`
                          }
                        >
                          <span className="text-base w-6 text-center flex-shrink-0">{item.icon}</span>
                          <span className="flex-1 text-[13px]">{t(item.tKey)}</span>
                          {locked && (
                            <span className="text-xs text-amber-500 dark:text-amber-400 flex-shrink-0" title={t('sidebarLockedFeatureTitle') || 'Bu özellik planınıza dahil değil'}>🔒</span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </nav>

        {/* Bottom actions */}
        <div className="p-3 border-t border-gray-200 dark:border-gray-800 space-y-1">
          <button
            onClick={toggleTheme}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            aria-label={isDark ? t('theme_light') : t('theme_dark')}
          >
            <span className="text-lg w-6 text-center" aria-hidden="true">{isDark ? '☀️' : '🌙'}</span>
            {isDark ? t('theme_light') : t('theme_dark')}
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <span className="text-lg w-6 text-center">🚪</span>
            {t('logout')}
          </button>
        </div>
      </aside>

      {/* Mobile bottom nav */}
      <nav
        role="navigation"
        aria-label={t('mobileBottomNav') || 'Mobil hızlı menü'}
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 shadow-lg safe-area-bottom"
      >
        <div className="flex items-center justify-around px-2 py-1">
          {navItems.slice(0, 5).map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              aria-label={t(item.tKey)}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-2 py-2 rounded-xl min-w-[52px] transition-all ${
                  isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span aria-hidden="true" className={`text-xl transition-transform ${isActive ? 'scale-110' : ''}`}>{item.icon}</span>
                  <span className="text-[9px] font-semibold leading-none">{t(item.tKey)}</span>
                </>
              )}
            </NavLink>
          ))}
          {navItems.length > 5 && (
            <button
              onClick={() => setOpen(true)}
              aria-label={t('openMenu') || 'Daha fazla'}
              className="flex flex-col items-center gap-0.5 px-2 py-2 rounded-xl min-w-[52px] text-gray-400 dark:text-gray-500"
            >
              <span aria-hidden="true" className="text-xl">⋯</span>
              <span className="text-[9px] font-semibold leading-none">{t('navMore') || 'Daha'}</span>
            </button>
          )}
        </div>
      </nav>
    </>
  );
}
